import { useMemo, useState } from "react";
import { MapPin, Calendar, Users, Search, ArrowLeftRight } from "lucide-react";
import { buyLink } from "@/data";
import { originsWithService, destinationsFrom } from "@/connections";

const PAX = ["1 pasajero", "2 pasajeros", "3 pasajeros", "4 pasajeros", "5 pasajeros"];

export default function SearchWidget() {
    const [origin, setOrigin] = useState("");
    const [destination, setDestination] = useState("");
    const [date, setDate] = useState("");
    const [pax, setPax] = useState(PAX[0]);

    const origins = useMemo(() => originsWithService(), []);
    const destinations = useMemo(
        () =>
            origin
                ? destinationsFrom(origin).map((d) => d.to)
                : originsWithService(),
        [origin],
    );

    const onOrigin = (v) => {
        setOrigin(v);
        if (v && !destinationsFrom(v).some((d) => d.to === destination)) {
            setDestination("");
        }
    };

    const swap = () => {
        const o = origin,
            d = destination;
        setOrigin(destinationsFrom(d).length ? d : "");
        setDestination(o);
    };

    const submit = (e) => {
        e.preventDefault();
        window.open(buyLink(destination || origin), "_blank", "noopener");
    };

    const Field = ({ children, className = "" }) => (
        <div
            className={`relative flex-1 min-w-[150px] rounded-2xl px-4 py-2.5 md:rounded-none md:border-r md:border-black/10 bg-black/[0.02] md:bg-transparent ${className}`}
        >
            {children}
        </div>
    );

    return (
        <form
            onSubmit={submit}
            data-testid="search-widget"
            className="rounded-[26px] bg-white p-3 shadow-2xl shadow-black/20 ring-1 ring-black/5"
        >
            <div className="flex flex-col md:flex-row md:items-stretch gap-2 md:gap-0">
                <Field>
                    <CustomField
                        label="Origen"
                        icon={MapPin}
                        value={origin}
                        onChange={onOrigin}
                        options={origins}
                        placeholder="¿Desde dónde?"
                        testId="search-origin"
                    />
                </Field>

                <div className="hidden md:flex items-center justify-center px-1">
                    <button
                        type="button"
                        onClick={swap}
                        data-testid="search-swap"
                        aria-label="Intercambiar"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-black/10 text-brand transition-colors hover:bg-brand hover:text-white"
                    >
                        <ArrowLeftRight className="h-4 w-4" />
                    </button>
                </div>

                <Field>
                    <CustomField
                        label="Destino"
                        icon={MapPin}
                        value={destination}
                        onChange={setDestination}
                        options={destinations}
                        placeholder="¿Hacia dónde?"
                        testId="search-destination"
                    />
                </Field>

                <Field>
                    <label className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-brand">
                        <Calendar className="h-3.5 w-3.5" /> Fecha
                    </label>
                    <input
                        type="text"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        onFocus={(e) => (e.target.type = "date")}
                        onBlur={(e) => {
                            if (!e.target.value) e.target.type = "text";
                        }}
                        placeholder="Elegí la fecha"
                        data-testid="search-date"
                        className="mt-1 w-full bg-transparent text-sm font-medium text-ink outline-none cursor-pointer placeholder:text-ink/50"
                    />
                </Field>

                <Field>
                    <CustomField
                        label="Pasajeros"
                        icon={Users}
                        value={pax}
                        onChange={setPax}
                        options={PAX}
                        placeholder="Pasajeros"
                        testId="search-pax"
                    />
                </Field>

                <button
                    type="submit"
                    data-testid="search-submit"
                    className="mt-1 md:mt-0 inline-flex items-center justify-center gap-2 rounded-2xl bg-brand px-7 py-3.5 text-sm font-bold uppercase tracking-wide text-white transition-colors hover:bg-brand-dark md:ml-2"
                >
                    <Search className="h-4 w-4" />
                    Buscar pasajes
                </button>
            </div>
        </form>
    );
}

// Lightweight wrapper to reuse CustomSelect with the field label styling
import CustomSelect from "@/components/CustomSelect";
function CustomField(props) {
    return <CustomSelect {...props} />;
}
